/**
 * ONE ANSWER TO "DID THIS CLASS MEET ON THIS DAY?", FOR EVERY READER.
 *
 * Bobbie, 2026-10-23, choosing Option A of the class cancellation design
 * (after #1382's "Class was cancelled" failed review three times because the
 * readers answered this three different ways): the cancellation record
 * classCancellations/{offeringId}_{date} is the only thing anybody asks.
 *
 * - A record whose state is 'reinstated' is a cancel somebody undid. The class
 *   met (or will), and every mark that night counts again, everywhere at once.
 * - Any other record is a cancel that stands. Marks saved for that day stay on
 *   their documents, untouched, and no reader counts them.
 * - No record, no cancel.
 *
 * Marks are never stamped, deleted or rewritten by a cancel or an undo, so
 * there is nothing on them that can drift from the record. A record written
 * before `state` existed is resolved once by the scheduled backfill
 * (classCancellationStateBackfill.mjs); until then it reads as a cancel that
 * stands, the same thing the Attendance page and the reminder already did with
 * it when no booking was made after it.
 *
 * Imports nothing but the pure attendanceSubmit.ts date check, so the client
 * hooks and the Netlify functions share it.
 * tests/cancelled-attendance-readers.test.mjs fails if a reader that counts
 * attendance stops asking it.
 */

import { isRealDateKey } from './attendanceSubmit.ts';

export type ClassCancellationState = 'cancelled' | 'reinstated';

/** The fields of a classCancellations document this rule reads. */
export interface CancellationStateInput {
  offeringId?: unknown;
  date?: unknown;
  state?: unknown;
}

/**
 * The code a booking refused for a day whose cancel stands carries
 * (reservations.ts useCreateReservation), so a screen can offer "Undo the
 * cancel and book it?" instead of a dead end.
 */
export const CLASS_DAY_CANCELLED_CODE = 'class_day_cancelled';

/** The class and day a CLASS_DAY_CANCELLED_CODE refusal names, or null for any other error. */
export function cancelledDayFromError(error: unknown): { offeringId: string; date: string } | null {
  if (!error || typeof error !== 'object') return null;
  const e = error as { code?: unknown; offeringId?: unknown; date?: unknown };
  if (e.code !== CLASS_DAY_CANCELLED_CODE) return null;
  return typeof e.offeringId === 'string' && typeof e.date === 'string'
    ? { offeringId: e.offeringId, date: e.date }
    : null;
}

/** The classCancellations/{id} key for one class on one studio day. */
export function cancelledDayKey(offeringId: string, date: string): string {
  return `${offeringId}_${date}`;
}

/** Does this record mean the class did not meet? Null or undefined: no record. */
export function isCancellationActive(record: CancellationStateInput | null | undefined): boolean {
  if (!record) return false;
  return record.state !== 'reinstated';
}

/** The class-day keys whose cancel stands, from a set of records. */
export function activeCancelledDayKeys(records: readonly CancellationStateInput[]): Set<string> {
  const keys = new Set<string>();
  for (const record of records) {
    if (!isCancellationActive(record)) continue;
    if (typeof record.offeringId === 'string' && record.offeringId
      && typeof record.date === 'string' && record.date) {
      keys.add(cancelledDayKey(record.offeringId, record.date));
    }
  }
  return keys;
}

/** Does this mark count toward attended or missed? */
export function markCounts(
  mark: { offeringId?: unknown; date?: unknown } | null | undefined,
  cancelledDays: ReadonlySet<string>
): boolean {
  if (!mark) return false;
  if (cancelledDays.size === 0) return true;
  if (typeof mark.offeringId !== 'string' || typeof mark.date !== 'string') return true;
  return !cancelledDays.has(cancelledDayKey(mark.offeringId, mark.date));
}

/** The marks that count. The same array comes back on a week with no cancels. */
export function countableMarks<T extends { offeringId?: unknown; date?: unknown }>(
  marks: readonly T[],
  cancelledDays: ReadonlySet<string>
): T[] {
  if (cancelledDays.size === 0) return marks as T[];
  return marks.filter((mark) => markCounts(mark, cancelledDays));
}

/**
 * HOW LONG A TEACHER MAY STILL CHANGE A NIGHT'S REGISTER, in days after the
 * class day. The SAME number shared/firestore.rules gives a teacher to create
 * or correct a mark (isWithinTeacherAttendanceWindow, `duration.value(10,
 * 'd')` from midnight UTC of the class date). A teacher or sub may record a
 * class as cancelled, or undo her own record, only inside it; a Director or
 * Manager is not bound by it, exactly as the rules leave a Director unbound.
 * tests/cancelled-attendance-readers.test.mjs fails if the two differ.
 */
export const ATTENDANCE_TEACHER_WINDOW_DAYS = 10;

/** True while a teacher may still change this date's register, by the rules' own clock. */
export function withinTeacherAttendanceWindow(dateKey: string, nowMs: number): boolean {
  if (!isRealDateKey(dateKey)) return false;
  const [y, m, d] = dateKey.split('-').map(Number);
  return nowMs <= Date.UTC(y, m - 1, d) + ATTENDANCE_TEACHER_WINDOW_DAYS * 24 * 60 * 60 * 1000;
}
